import React, { createContext, useContext, useReducer, useCallback } from "react";

// ─── Module-level event bus ──────────────────────────────────────────
// Allows IsLoggedIn.js to route incoming socket messages to open MiniChats
// without prop-drilling through the React tree.
export const miniChatBus = {
  _handlers: new Map(),
  _openChatHandler: null,
  _pendingMessages: new Map(),
  subscribe(chatId, fn) {
    this._handlers.set(chatId, fn);
  },
  unsubscribe(chatId) {
    this._handlers.delete(chatId);
  },
  registerOpenChat(fn) {
    this._openChatHandler = fn;
  },
  unregisterOpenChat(fn) {
    if (!fn || this._openChatHandler === fn) {
      this._openChatHandler = null;
    }
  },
  openChat(user) {
    if (this._openChatHandler && user) {
      this._openChatHandler(user);
    }
  },
  queueMessage(message) {
    const senderId = String(message?.sender?._id || message?.sender?.id || "");
    if (!senderId) return;
    const queued = this._pendingMessages.get(senderId) || [];
    queued.push(message);
    this._pendingMessages.set(senderId, queued);
  },
  drainQueuedMessages(chatId) {
    const id = String(chatId || "");
    if (!id) return [];
    const queued = this._pendingMessages.get(id) || [];
    this._pendingMessages.delete(id);
    return queued;
  },
  /** Check if any open mini chat matches the sender */
  hasChatForSender(senderId) {
    return this._handlers.has(String(senderId));
  },
  /** Broadcast an incoming message to all open mini chats */
  emit(message) {
    for (const [, fn] of this._handlers) {
      fn(message);
    }
  },
};

// ─── Reducer ─────────────────────────────────────────────────────────
const MAX_OPEN_CHATS = 3;

function reducer(state, action) {
  switch (action.type) {
    case "OPEN_CHAT": {
      const { user } = action.payload;
      const id = String(user._id || user.id);
      // If already open, just un-minimize it
      const existing = state.find((c) => c.id === id);
      if (existing) {
        return state.map((c) => (c.id === id ? { ...c, minimized: false } : c));
      }
      // If at max, close the leftmost (oldest) one
      const trimmed = state.length >= MAX_OPEN_CHATS ? state.slice(1) : state;
      return [
        ...trimmed,
        { id, user, conversationId: null, minimized: false },
      ];
    }
    case "CLOSE_CHAT": {
      return state.filter((c) => c.id !== action.payload);
    }
    case "TOGGLE_MINIMIZE": {
      return state.map((c) =>
        c.id === action.payload ? { ...c, minimized: !c.minimized } : c
      );
    }
    case "SET_CONVERSATION_ID": {
      const { chatId, conversationId } = action.payload;
      return state.map((c) =>
        c.id === chatId ? { ...c, conversationId } : c
      );
    }
    case "HYDRATE": {
      // Replace state from another tab's persisted snapshot (storage event).
      return Array.isArray(action.payload) ? action.payload : state;
    }
    default:
      return state;
  }
}

// ─── Persistence ─────────────────────────────────────────────────────
// Open chatboxes survive full page loads and NEW TABS: the open-chat list is
// mirrored to localStorage (keyed per signed-in user so accounts never bleed
// into each other) and re-hydrated on mount. A `storage` listener live-syncs
// open/close/minimize across already-open tabs. Where the boxes are allowed
// to RENDER is decided by ChatPopupManager (all clan surfaces; never the
// /account page or the /app platform console).

function miniChatStorageKey(userId) {
  return `uniflo.openMiniChats.v1.${userId || "anon"}`;
}

// Persist only the fields MiniChat actually renders from `chat.user` —
// never the full profile object (keeps the snapshot small and PII-light).
function sanitizeChatForStorage(chat) {
  const u = chat?.user || {};
  return {
    id: chat.id,
    conversationId: chat.conversationId || null,
    minimized: Boolean(chat.minimized),
    user: {
      _id: u._id || u.id || chat.id,
      id: u.id || u._id || chat.id,
      guid: u.guid,
      first_name: u.first_name || u.firstName || "",
      last_name: u.last_name || u.lastName || "",
      firstName: u.firstName || u.first_name || "",
      lastName: u.lastName || u.last_name || "",
      username: u.username || "",
      displayName: u.displayName || "",
      photo: u.photo || "",
      isSystemAi: Boolean(u.isSystemAi),
    },
  };
}

function readPersistedChats(userId) {
  try {
    const raw = window.localStorage.getItem(miniChatStorageKey(userId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((c) => c && c.id && c.user)
      .slice(0, MAX_OPEN_CHATS);
  } catch {
    return [];
  }
}

function writePersistedChats(userId, chats) {
  try {
    window.localStorage.setItem(
      miniChatStorageKey(userId),
      JSON.stringify((chats || []).map(sanitizeChatForStorage))
    );
  } catch {
    // storage full / private mode — chatboxes just won't persist
  }
}

// ─── Context ─────────────────────────────────────────────────────────
const MiniChatContext = createContext(null);

export function MiniChatProvider({ user, children }) {
  const currentUserId = String(user?._id || user?.id || "");
  const [openChats, dispatch] = useReducer(
    reducer,
    currentUserId,
    readPersistedChats
  );

  // Mirror every change to localStorage so a brand-new tab restores the
  // same open chatboxes.
  React.useEffect(() => {
    writePersistedChats(currentUserId, openChats);
  }, [currentUserId, openChats]);

  // Live-sync across tabs: `storage` fires only in OTHER tabs, so hydrating
  // from it can't loop.
  React.useEffect(() => {
    const key = miniChatStorageKey(currentUserId);
    const onStorage = (event) => {
      if (event.key !== key) return;
      dispatch({
        type: "HYDRATE",
        payload: readPersistedChats(currentUserId),
      });
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [currentUserId]);

  const openMiniChat = useCallback(
    (friend) => dispatch({ type: "OPEN_CHAT", payload: { user: friend } }),
    []
  );

  const closeMiniChat = useCallback(
    (chatId) => dispatch({ type: "CLOSE_CHAT", payload: chatId }),
    []
  );

  const toggleMinimize = useCallback(
    (chatId) => dispatch({ type: "TOGGLE_MINIMIZE", payload: chatId }),
    []
  );

  const setConversationId = useCallback(
    (chatId, conversationId) =>
      dispatch({
        type: "SET_CONVERSATION_ID",
        payload: { chatId, conversationId },
      }),
    []
  );

  const value = {
    openChats,
    currentUser: user,
    openMiniChat,
    closeMiniChat,
    toggleMinimize,
    setConversationId,
  };

  React.useEffect(() => {
    miniChatBus.registerOpenChat(openMiniChat);
    return () => miniChatBus.unregisterOpenChat(openMiniChat);
  }, [openMiniChat]);

  return (
    <MiniChatContext.Provider value={value}>
      {children}
    </MiniChatContext.Provider>
  );
}

export function useMiniChat() {
  const ctx = useContext(MiniChatContext);
  if (!ctx) throw new Error("useMiniChat must be used within MiniChatProvider");
  return ctx;
}

/**
 * Same context, but returns null instead of throwing when no provider is
 * mounted above. For components that render on both chat-enabled routes and
 * routes outside MiniChatProvider — the clan admin console being the case that
 * forced this: mounting the shared people rail there crashed every
 * /administrator page into the error boundary.
 *
 * Callers must degrade honestly when this returns null: hide or disable the
 * affordance rather than rendering a control that silently does nothing.
 */
export function useMiniChatOptional() {
  return useContext(MiniChatContext) || null;
}
